import { useState } from "react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { toast } from "sonner";
import {
  Images, Sparkles, Plus, Trash2, ArrowUp, ArrowDown,
  Download, Loader2, Check, Type, Image as ImageIcon,
} from "lucide-react";
import { articlesApi } from "../lib/api";

const FONT_STYLES = [
  { id: "moderna", label: "Moderna", sample: "Aa" },
  { id: "clasica", label: "Clásica", sample: "Aa" },
  { id: "cursiva", label: "Cursiva", sample: "Aa" },
  { id: "elegante", label: "Elegante", sample: "Aa" },
  { id: "bold", label: "Bold", sample: "AA" },
  { id: "manuscrita", label: "Manuscrita", sample: "Aa" },
];

const FORMATS = [
  { id: "4:5", label: "4:5 Carrusel", hint: "Feed / Post" },
  { id: "9:16", label: "9:16 Reels", hint: "Stories / Reels" },
];

export default function SocialImagesGenerator({ articleId }) {
  const [step, setStep] = useState(1); // 1=phrases, 2=config, 3=results
  const [loading, setLoading] = useState(false);
  const [phrases, setPhrases] = useState([]);
  const [phraseCount, setPhraseCount] = useState(5);
  const [formats, setFormats] = useState(["4:5"]);
  const [fontStyle, setFontStyle] = useState("moderna");
  const [scenes, setScenes] = useState([]); // per-phrase scenes
  const [includeLogo, setIncludeLogo] = useState(false);
  const [jobProgress, setJobProgress] = useState(0);
  const [results, setResults] = useState(null); // { "4:5": [{image_base64, phrase}], "9:16": [...] }

  const extractPhrases = async () => {
    setLoading(true);
    try {
      const res = await articlesApi.extractPhrases(articleId, phraseCount);
      const ps = res.data.phrases || [];
      setPhrases(ps);
      setScenes(ps.map(() => ""));
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al extraer frases");
    } finally {
      setLoading(false);
    }
  };

  const updatePhrase = (i, text) => {
    const copy = [...phrases];
    copy[i] = text;
    setPhrases(copy);
  };
  const removePhrase = (i) => {
    if (phrases.length <= 3) {
      toast.warning("Mínimo 3 frases");
      return;
    }
    setPhrases(phrases.filter((_, idx) => idx !== i));
    setScenes(scenes.filter((_, idx) => idx !== i));
  };
  const move = (i, dir) => {
    const target = i + dir;
    if (target < 0 || target >= phrases.length) return;
    const np = [...phrases], ns = [...scenes];
    [np[i], np[target]] = [np[target], np[i]];
    [ns[i], ns[target]] = [ns[target], ns[i]];
    setPhrases(np);
    setScenes(ns);
  };
  const toggleFormat = (f) => {
    setFormats((cur) => cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f]);
  };

  const pollJob = async (jobId) => {
    for (let i = 0; i < 40; i++) {
      await new Promise((r) => setTimeout(r, 5000));
      setJobProgress(Math.min(95, (i / 40) * 100));
      try {
        const res = await articlesApi.socialImagesStatus(articleId, jobId);
        if (res.data.status === "completed") return res.data;
        if (res.data.status === "failed") throw new Error(res.data.error || "Generación fallida");
      } catch (e) {
        if (e.response?.status === 404) throw new Error("Job no encontrado");
      }
    }
    throw new Error("Timeout: la generación está tardando demasiado");
  };

  const generate = async () => {
    if (phrases.length < 3 || phrases.length > 5) {
      toast.error("Necesitas entre 3 y 5 frases");
      return;
    }
    if (formats.length === 0) {
      toast.error("Selecciona al menos un formato");
      return;
    }
    setLoading(true);
    setJobProgress(5);
    try {
      const cards = phrases.map((p, i) => ({ phrase: p.trim(), scene: (scenes[i] || "").trim() }));
      const start = await articlesApi.generateSocialImages(articleId, {
        cards, formats, font_style: fontStyle, include_logo: includeLogo,
      });
      const { job_id, credits_used } = start.data;
      toast.info(`Generando ${cards.length * formats.length} imágenes · ${credits_used} créditos`);
      const done = await pollJob(job_id);
      setResults(done.images || {});
      setJobProgress(100);
      setStep(3);
      toast.success("Imágenes listas");
    } catch (err) {
      toast.error(err.message || err.response?.data?.detail || "Error al generar imágenes");
      setJobProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const downloadZip = async (fmt) => {
    if (!results || !results[fmt]) return;
    try {
      const payload = { [fmt]: results[fmt].map((x) => x.image_base64) };
      const res = await articlesApi.downloadSocialImagesZip(articleId, payload);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `imagenes-${fmt.replace(":", "x")}-${articleId.slice(0, 8)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
      toast.success(`ZIP ${fmt} descargado`);
    } catch {
      toast.error("Error al descargar el ZIP");
    }
  };

  const reset = () => {
    setStep(1);
    setResults(null);
    setJobProgress(0);
    setPhrases([]);
    setScenes([]);
  };

  return (
    <div data-testid="social-images-generator" className="rounded-xl border p-5 space-y-4"
      style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold flex items-center gap-2"
          style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
          <Images className="w-5 h-5" style={{ color: "#00D4FF" }} />
          Crear imágenes para redes
        </h3>
        {step > 1 && (
          <button onClick={reset} className="text-xs" style={{ color: "#64748B" }}>
            Volver a empezar
          </button>
        )}
      </div>

      {step === 1 && (
        <div className="space-y-3">
          <p className="text-xs" style={{ color: "#94A3B8", lineHeight: 1.6 }}>
            Claude extraerá frases de impacto del artículo. Luego podrás editarlas, elegir formato y estilo visual.
          </p>
          <div>
            <label className="text-xs block mb-1.5" style={{ color: "#94A3B8" }}>¿Cuántas frases?</label>
            <div className="flex gap-2">
              {[3, 4, 5].map((n) => (
                <button key={n} data-testid={`phrase-count-${n}`}
                  onClick={() => setPhraseCount(n)}
                  className="flex-1 h-9 rounded-lg border text-xs transition-all"
                  style={{
                    background: phraseCount === n ? "rgba(0,212,255,0.1)" : "transparent",
                    borderColor: phraseCount === n ? "rgba(0,212,255,0.4)" : "#1E3A5F",
                    color: phraseCount === n ? "#00D4FF" : "#94A3B8",
                  }}>
                  {n} frases
                </button>
              ))}
            </div>
          </div>
          <Button
            data-testid="extract-phrases-btn"
            onClick={extractPhrases}
            disabled={loading}
            className="w-full text-xs"
            style={{ background: "linear-gradient(135deg, #00B4D8, #00D4FF)", color: "#0B1622" }}>
            {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
            {loading ? "Extrayendo frases..." : "Extraer frases del artículo"}
          </Button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          {/* Phrases list — editable */}
          <div>
            <label className="text-xs block mb-2" style={{ color: "#94A3B8" }}>
              Frases ({phrases.length}) · edita, reordena o elimina
            </label>
            <div className="space-y-2">
              {phrases.map((phrase, i) => (
                <div key={i} className="rounded-lg border p-3" style={{ background: "#0B1622", borderColor: "#1E3A5F" }}>
                  <div className="flex items-start gap-2 mb-2">
                    <span className="text-xs px-2 py-0.5 rounded-full"
                      style={{ background: "rgba(0,212,255,0.1)", color: "#00D4FF" }}>
                      Frase {i + 1}
                    </span>
                    <div className="ml-auto flex gap-0.5">
                      <button data-testid={`phrase-up-${i}`} onClick={() => move(i, -1)}
                        disabled={i === 0} className="p-1 opacity-70 hover:opacity-100 disabled:opacity-30">
                        <ArrowUp className="w-3.5 h-3.5" style={{ color: "#94A3B8" }} />
                      </button>
                      <button data-testid={`phrase-down-${i}`} onClick={() => move(i, 1)}
                        disabled={i === phrases.length - 1} className="p-1 opacity-70 hover:opacity-100 disabled:opacity-30">
                        <ArrowDown className="w-3.5 h-3.5" style={{ color: "#94A3B8" }} />
                      </button>
                      <button data-testid={`phrase-remove-${i}`} onClick={() => removePhrase(i)}
                        className="p-1 opacity-70 hover:opacity-100">
                        <Trash2 className="w-3.5 h-3.5" style={{ color: "#f87171" }} />
                      </button>
                    </div>
                  </div>
                  <Textarea
                    data-testid={`phrase-input-${i}`}
                    value={phrase}
                    onChange={(e) => updatePhrase(i, e.target.value)}
                    rows={2}
                    maxLength={200}
                    className="text-xs resize-none"
                    style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#E2E8F0" }}
                  />
                  <Input
                    data-testid={`phrase-scene-${i}`}
                    value={scenes[i] || ""}
                    onChange={(e) => {
                      const ns = [...scenes]; ns[i] = e.target.value; setScenes(ns);
                    }}
                    placeholder="Fondo / escena (ej: playa al amanecer, oficina moderna, pareja abrazándose)"
                    className="mt-2 h-8 text-xs"
                    style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#E2E8F0" }}
                  />
                </div>
              ))}
            </div>
            {phrases.length < 5 && (
              <button data-testid="add-phrase-btn"
                onClick={() => { setPhrases([...phrases, ""]); setScenes([...scenes, ""]); }}
                className="mt-2 text-xs flex items-center gap-1" style={{ color: "#00D4FF" }}>
                <Plus className="w-3.5 h-3.5" /> Añadir frase
              </button>
            )}
          </div>

          {/* Format */}
          <div>
            <label className="text-xs block mb-2" style={{ color: "#94A3B8" }}>Formato</label>
            <div className="grid grid-cols-2 gap-2">
              {FORMATS.map((f) => {
                const active = formats.includes(f.id);
                return (
                  <button key={f.id} data-testid={`format-${f.id}`}
                    onClick={() => toggleFormat(f.id)}
                    className="rounded-lg p-3 border text-left transition-all"
                    style={{
                      background: active ? "rgba(0,212,255,0.08)" : "#0B1622",
                      borderColor: active ? "rgba(0,212,255,0.4)" : "#1E3A5F",
                    }}>
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-semibold" style={{ color: active ? "#00D4FF" : "#E2E8F0" }}>{f.label}</div>
                      {active && <Check className="w-3.5 h-3.5" style={{ color: "#00D4FF" }} />}
                    </div>
                    <div className="text-xs mt-0.5" style={{ color: "#64748B" }}>{f.hint}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Font style */}
          <div>
            <label className="text-xs block mb-2" style={{ color: "#94A3B8" }}>
              <Type className="w-3 h-3 inline mr-1" />
              Estilo de letra
            </label>
            <div className="grid grid-cols-3 gap-2">
              {FONT_STYLES.map((fs) => {
                const active = fontStyle === fs.id;
                return (
                  <button key={fs.id} data-testid={`font-${fs.id}`}
                    onClick={() => setFontStyle(fs.id)}
                    className="rounded-lg p-2 border text-xs transition-all"
                    style={{
                      background: active ? "rgba(0,212,255,0.08)" : "#0B1622",
                      borderColor: active ? "rgba(0,212,255,0.4)" : "#1E3A5F",
                      color: active ? "#00D4FF" : "#94A3B8",
                    }}>
                    {fs.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Logo toggle */}
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => setIncludeLogo(!includeLogo)}>
            <div
              data-testid="include-logo-toggle"
              className="w-4 h-4 rounded flex items-center justify-center border flex-shrink-0"
              style={{
                background: includeLogo ? "#00D4FF" : "transparent",
                borderColor: includeLogo ? "#00D4FF" : "#334155",
              }}>
              {includeLogo && <Check className="w-3 h-3" style={{ color: "#0B1622" }} />}
            </div>
            <ImageIcon className="w-3.5 h-3.5" style={{ color: includeLogo ? "#00D4FF" : "#64748B" }} />
            <span className="text-xs" style={{ color: "#E2E8F0" }}>
              Incluir mi logo (esquina superior derecha)
            </span>
          </div>

          <Button
            data-testid="generate-social-images-btn"
            onClick={generate}
            disabled={loading || phrases.length < 3}
            className="w-full text-xs"
            style={{ background: "linear-gradient(135deg, #00B4D8, #00D4FF)", color: "#0B1622" }}>
            {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
            {loading ? `Generando imágenes... ${Math.round(jobProgress)}%` : `Generar ${phrases.length * formats.length} imágenes`}
          </Button>

          {loading && jobProgress > 0 && (
            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "#1E3A5F" }}>
              <div
                data-testid="social-images-progress"
                className="h-full transition-all duration-500"
                style={{
                  width: `${jobProgress}%`,
                  background: "linear-gradient(90deg, #00B4D8, #00D4FF)",
                }}
              />
            </div>
          )}
        </div>
      )}

      {step === 3 && results && (
        <div className="space-y-4">
          <p className="text-xs" style={{ color: "#4ADE80" }}>
            ¡Listo! Tus imágenes están generadas.
          </p>
          {Object.entries(results).map(([fmt, imgs]) => (
            <div key={fmt} className="rounded-lg border p-3" style={{ background: "#0B1622", borderColor: "#1E3A5F" }}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold" style={{ color: "#E2E8F0" }}>
                  Formato {fmt} · {imgs.length} imágenes
                </span>
                <Button
                  data-testid={`download-zip-${fmt.replace(":", "x")}`}
                  onClick={() => downloadZip(fmt)}
                  size="sm"
                  className="text-xs h-8"
                  style={{ background: "linear-gradient(135deg, #00B4D8, #00D4FF)", color: "#0B1622" }}>
                  <Download className="w-3.5 h-3.5 mr-1" />
                  Descargar imágenes {fmt}
                </Button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {imgs.map((img, i) => (
                  <div key={i} className="rounded-md overflow-hidden border"
                    style={{ borderColor: "#1E3A5F", aspectRatio: fmt === "4:5" ? "4/5" : "9/16" }}>
                    <img
                      src={`data:image/png;base64,${img.image_base64}`}
                      alt={`card ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
